import { useState, useEffect, useCallback, useRef } from 'react';
import { usersService } from '../services/api.users';
import useAuth from './useAuth';

export const useGridSettings = (tableKey) => {
    const { user } = useAuth();
    const [settings, setSettings] = useState({});
    const [filters, setFilters] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const saveTimerRef = useRef(null);

    const load = useCallback(async (showLoading = false) => {
        if (!tableKey || !user?.id) {
            setIsLoading(false);
            return;
        }
        if (showLoading) setIsLoading(true);
        try {
            const data = await usersService.getColumnSettings(tableKey);
            setSettings(data?.columns || {});
            if (data?.filtersJson) {
                setFilters(JSON.parse(data.filtersJson));
            } else {
                setFilters({});
            }
        } catch (err) {
            console.warn('Failed to load grid settings:', err);
        } finally {
            setIsLoading(false);
        }
    }, [tableKey, user?.id]);

    useEffect(() => {
        load(true);
    }, [load]);

    // Дебаунс отправки настроек на сервер (500мс) для исключения спама запросами
    const saveSettings = useCallback(async (newColumns, newFilters) => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

        saveTimerRef.current = setTimeout(async () => {
            try {
                const payload = {
                    tableKey: tableKey,
                    columns: newColumns,
                    filters: newFilters
                };

                await usersService.saveColumnSettings(payload);
            } catch (err) {
                console.error('Ошибка сохранения:', err);
            }
        }, 500);
    }, [tableKey]);

    // Применение сохраненных настроек (видимость, порядок, ширина) к колонкам
    const applyToColumns = useCallback((columns) => {
        if (!columns || isLoading) return columns;

        return columns
            .map(col => {
                const s = settings[col.name];
                
                // 1. Если у самой колонки в конфигурации
                //    оно имеет наивысший приоритет.
                let visible;
                if (typeof col.visible === 'boolean') {
                    visible = col.visible;
                } else {
                    visible = (s?.visible === true || s?.visible === false)
                        ? s.visible
                        : (col.defaultVisible ?? true);
                }

                // Проверяем, сохранил ли пользователь фиксированную ширину для этой колонки
                const hasSavedWidth = s?.width !== undefined && s?.width !== null;

                return {
                    ...col,
                    visible,
                    // сбрасываем defaultFlex/flex, иначе адаптивность flexbox заблокирует жесткий width.
                    defaultFlex: hasSavedWidth ? undefined : col.defaultFlex,
                    flex: hasSavedWidth ? undefined : col.flex,
                    width: hasSavedWidth ? s.width : col.width,
                    order: s?.order ?? col.order
                };
            })
            .sort((a, b) => {
                const orderA = settings[a.name]?.order ?? a.order ?? 999;
                const orderB = settings[b.name]?.order ?? b.order ?? 999;
                return orderA - orderB;
            });
    }, [settings, isLoading]);

    // Изменение порядка колонок
    const onColumnReorder = useCallback((columnNames, columns = []) => {
        if (!Array.isArray(columnNames)) return;

        setSettings(prev => {
            const updated = { ...prev };
            columnNames.forEach((columnName, index) => {
                if (!columnName) return;

                const col = columns.find(c => c.name === columnName);
                const savedVisible = prev[columnName]?.visible;
                const visible = (savedVisible === true || savedVisible === false)
                    ? savedVisible
                    : (col?.defaultVisible ?? true);

                updated[columnName] = {
                    ...prev[columnName],
                    order: index,
                    visible
                };
            });

            saveSettings(updated);
            return updated;
        });
    }, [saveSettings]);

    // Переключение видимости колонки
    const onVisibilityChange = useCallback((payload) => {
        const columnName = payload?.name || payload?.[0]?.name;
        const visible = payload?.visible ?? payload?.[0]?.visible;

        if (!columnName) return;

        setSettings(prev => {
            const updated = {
                ...prev,
                [columnName]: { ...prev[columnName], visible }
            };
            saveSettings(updated);
            return updated;
        });
    }, [saveSettings]);

    // Обработчик изменения ширины колонки
    const onColumnResize = useCallback((payload) => {
        const colName = payload?.column?.name || payload?.name;
        const colWidth = payload?.size ?? payload?.width;

        // Защита: если имя колонки или размер не определены, прерываем выполнение
        if (!colName || colWidth === undefined || colWidth === null) return;

        setSettings(prev => {
            // если размер не изменился, стейт не обновляем.
            if (prev[colName]?.width === colWidth) return prev;

            const updated = {
                ...prev,
                [colName]: {
                    ...prev[colName],
                    width: colWidth
                }
            };

            saveSettings(updated);
            return updated;
        });
    }, [saveSettings]);

    const onFilterChange = useCallback((filterArray) => {
        // Преобразуем массив фильтров Inovua в простой объект { name: value }
        const simpleFilters = filterArray.reduce((acc, f) => {
            if (f.value !== undefined && f.value !== null) {
                acc[f.name] = f.value;
            }
            return acc;
        }, {});

        setFilters(simpleFilters);
        saveSettings(settings, simpleFilters); // Сохраняем упрощенный вид
    }, [settings, saveSettings]);

    return {
        applyToColumns,
        onVisibilityChange,
        onColumnReorder,
        onColumnResize,
        isLoading,
        settings,
        filters,
        onFilterChange,
        refetch: () => load(true),
    };
};